import React from 'react';
import isEqual from 'react-fast-compare';
import {connect} from 'react-redux';
import _ from 'underscore';

import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import getUserFacingPolicies from 'auth/getUserFacingPolicies';
import {getAdminDataPolicy, getDefaultDataPolicy} from 'auth/IntrinsicPolicies';
import {
  Collaborator,
  DataPolicyCollaborator,
  GroupCollaborator,
  Owner,
  OwnerInfo,
} from 'auth/PermissionConstants';
import {
  convertCollaboratorToCollaborations,
  convertOwnerToCollaboration,
  getCollaboratorMemberPolicyId,
  isPartyAdmin,
} from 'auth/PermissionUtil';
import {DataPolicy} from 'auth/PolicyConstants';
import BenchlingConfig from 'BenchlingConfig';
import {CollaborationManagementStateQuery} from 'collaborator-components/__generated/CollaborationManagementStateQuery';
import {NONE_POLICY} from 'collaborator-components/CollaborationManagementContent';
import CollaborationManagementSection from 'collaborator-components/CollaborationManagementSection';
import {UpdateArgs} from 'collaborator-components/CollaboratorsSection';
import OwnableCollaboratorsSection from 'collaborator-components/OwnableCollaboratorsSection';
import useCollaborationManagementState, {
  Collaboration,
  CollaborationManagementActions,
  CollaborationManagementState,
} from 'collaborator-components/useCollaborationManagementState';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FullSource, Source} from 'constants/SourceConstants';
import {isUser} from 'constants/UserConstants';
import {getOwnerChoices, getSelectOwnerHelpMessage, OwnershipType} from 'create-components/OwnableUtil';
import SelectOwnerField from 'create-components/SelectOwnerField';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import navigateToURL from 'lib/navigateToURL';
import {confirmReviewLifecycleRetractionIfNeeded} from 'notebook/reviews/review-lifecycles/RetractReviewsConfirmationModal';
import notifications, {Response} from 'notifications';
import {hasAllOrgsWithDisablePersonalOwnables} from 'orgs/DisablePersonalOwnablesUtil';
import {Organization, OrganizationPermissionRole, Team, TeamPermissionRole} from 'orgs/OrganizationConstants';
import {
  isPrincipalType,
  Party,
  PARTY_TYPE_TO_GRAPHQL_TYPENAME,
  PRINCIPAL_TYPE_TO_GRAPHQL_TYPENAME,
  PrincipalTypename,
} from 'parties/PartyConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import FormInput from 'ui-library/bs/FormInput';
import Label from 'ui-library/bs/Label';
import LegacyFieldError from 'ui-library/bs/LegacyFieldError';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import Modal from 'ui-library/modals/Modal';
import * as modals from 'util/Modals';
import {capitalize, capwords, typeToName} from 'util/NameUtil';
import {InputChangeEvent} from 'util/Types';

import {OwnableInfo, UpdateOwnableParams} from './OwnableConstants';
import ReviewLifecycleAndCollaboratorsSection from './ReviewProcessAndCollaboratorsSection';

type Group = ExtractByTypename<
  ExtractByTypename<
    CollaborationManagementStateQuery['policeable'],
    'EntryNode'
  >['inheritedCollaborations'][number]['party'],
  'TeamNode'
>;

type Actor = ExtractByTypename<
  ExtractByTypename<
    CollaborationManagementStateQuery['policeable'],
    'EntryNode'
  >['inheritedCollaborations'][number]['party'],
  PrincipalTypename
>;

// TODO: Should return union type with party/owner and collaborator
const makeNewCollaborator = (
  party: Party | Owner,
  dataPolicy: DataPolicy | null,
  adminDataPolicy: DataPolicy | null = null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  if (party.type === ModelTypeConstants.USER) {
    return {...party, dataPolicyId: dataPolicy?.id ?? null};
  }
  return {
    ...party,
    memberDataPolicyId: dataPolicy?.id ?? null,
    adminDataPolicyId: adminDataPolicy?.id ?? null,
  };
};

const convertPartyToPartyNode = (party: Party | Owner): Group | Actor => {
  if (party.type === ModelTypeConstants.ORGANIZATION || party.type === ModelTypeConstants.TEAM) {
    return {
      __typename: PARTY_TYPE_TO_GRAPHQL_TYPENAME[party.type],
      id: party.id,
      name: party.name,
      handle: party.handle,
      avatarUrl: party.avatar_url,
      admin: isPartyAdmin(party),
    };
  }
  assert(isPrincipalType(party.type), 'Unexpected principal type');
  return {
    __typename: PRINCIPAL_TYPE_TO_GRAPHQL_TYPENAME[party.type],
    id: party.id,
    name: party.name,
    handle: party.handle,
    avatarUrl: party.avatar_url,
  };
};

const makeNewCollaboration = (
  party: Party | Owner,
  policy?: DataPolicy,
  adminDataPolicy?: DataPolicy
): Array<Collaboration> => {
  const partyNode = convertPartyToPartyNode(party);
  const newCollaborations: Array<Collaboration> = [];
  const baseCollaboration = {
    __typename: 'CollaborationNode' as const,
    party: partyNode,
    organizationRole: null,
    teamRole: null,
    auditor: null,
    resource: null,
    fromOwnership: null,
  };
  if (policy != null) {
    const collaboration: Collaboration = {
      ...baseCollaboration,
      policy: {
        __typename: 'DataPolicyNode',
        id: policy.id,
        name: policy.name,
        isHidden: false,
      },
      party: partyNode,
      organizationRole:
        partyNode.__typename === 'OrganizationNode' ? OrganizationPermissionRole.MEMBER : null,
      teamRole: partyNode.__typename === 'TeamNode' ? TeamPermissionRole.MEMBER : null,
    };
    newCollaborations.push(collaboration);
  }
  if (adminDataPolicy != null) {
    newCollaborations.push({
      ...baseCollaboration,
      policy: {
        __typename: 'DataPolicyNode',
        isHidden: false,
        id: adminDataPolicy.id,
        name: adminDataPolicy.name,
      },
      organizationRole: partyNode.__typename === 'OrganizationNode' ? OrganizationPermissionRole.ADMIN : null,
      teamRole: partyNode.__typename === 'TeamNode' ? TeamPermissionRole.ADMIN : null,
    });
  }
  return newCollaborations;
};

interface ConnectedProps {
  dataPolicies: Array<DataPolicy> | undefined;
}

function mapStateToProps(state: BenchlingState, props: OwnableSettingsModalProps): ConnectedProps {
  return {
    dataPolicies: getUserFacingPolicies(state.models.policies.dataPolicies!),
  };
}

// successCallback is currently written to work only for source use case
export interface SuccessCallbackParams {
  id: string;
  source: Source & Pick<FullSource, 'permissions'>;
  type: string;
  url: string;
}

interface WithReviewLifecycle {
  supportReviewLifecycleSelection: true;
  allowAuditorToggling: true;
  showWarningMessageOnReviewLifecycleEdit?: boolean;
}

export interface WithoutReviewLifecycle {
  supportReviewLifecycleSelection?: false;
  allowAuditorToggling?: boolean;
}

export type WithOrWithoutReviewLifecycle = WithReviewLifecycle | WithoutReviewLifecycle;

export interface WithCollaborationManagement {
  collaborationState?: CollaborationManagementState;
  collaborationActions?: CollaborationManagementActions;
  useCollaborationManagement?: boolean;
}

type CustomSettingsComponentType = React.ComponentType<{isPosting?: boolean; owner: Owner}>;

export interface BaseOwnableSettingsModalProps {
  title?: string;
  // doSubmit handler returns a different promise based on where it's called
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  doSubmit: (data: UpdateOwnableParams) => PromiseLike<any>;
  doArchive?: (() => void) | null;
  doUnarchive?: (() => void) | null;
  // if showConfirmDelete is true, doDelete must return a promise
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  doDelete?: (() => void | JQueryPromise<any>) | null;
  showConfirmDelete?: boolean;
  onLeave?: (partyId: string) => void;
  onUpdate?: () => void;
  onRequestClose: () => void;
  ownableType: string;
  navigateOnSuccess?: boolean;
  successCallback?: (data: SuccessCallbackParams) => void;
  enableName?: boolean;
  enableDescription?: boolean;
  disableSave?: boolean;
  isReadOnly?: boolean;
  disableChangeOwner?: boolean;
  // When disableChangeOwner is true, either disabledOwnerTooltipText or disabledOwnerHelpContent must be provided
  disabledOwnerTooltipText?: string;
  disabledOwnerHelpContent?: React.ReactNode;
  canManageCollaborators?: boolean;
  requiresCreatorOwnership?: boolean;
  existingOwnable?: OwnableInfo;
  ownableId?: string;
  customSettingsComponent?: CustomSettingsComponentType;
  initialCollaborators?: Array<Collaborator>;
  preferredOwnerId?: string;
  // Undefined means it's never been defined before, e.g. on create. Empty array means auditor based review.
  initialReviewLifecycleIds?: Array<string>;
  analyticsModule?: ANALYTICS_MODULE;
  analyticsModalType?: string;
  analyticsMetadata?: Record<string, unknown>;
}

export type OwnableSettingsModalProps = BaseOwnableSettingsModalProps &
  WithOrWithoutReviewLifecycle &
  WithCollaborationManagement;

const OwnableSettingsModalDefaultProps = {
  enableName: true,
  enableDescription: true,
  isReadOnly: false,
  navigateOnSuccess: true,
  showConfirmDelete: true,
};

interface OwnableSettingsModalErrors {
  name: string;
  description: string;
  owner: string;
}

interface OwnableSettingsModalState {
  posting: boolean;
  errors: OwnableSettingsModalErrors | null;
  name: string;
  description?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  collaborators: Array<any>;
  owner: Owner;
  ownerInfo: OwnerInfo;
  isAuditorBasedReview: boolean;
  reviewLifecycleIds: Array<string>;
}

/**
 * A React component that renders a modal for either creating an ownable or updating an existing one.
 * @package
 */
export class OwnableSettingsModal extends React.Component<OwnableSettingsModalProps & ConnectedProps> {
  static defaultProps = OwnableSettingsModalDefaultProps;

  constructor(props: OwnableSettingsModalProps & ConnectedProps) {
    super(props);

    if (props.disableChangeOwner) {
      assert(
        props.disabledOwnerTooltipText != null || props.disabledOwnerHelpContent != null,
        'disabledOwnerTooltipText or disabledOwnerHelpContent is required when disableChangeOwner is true'
      );
    }
  }

  private getInitialOwnerChoices(): Array<Owner> {
    if (this.props.requiresCreatorOwnership) {
      if (this.props.existingOwnable != null) {
        return [this.props.existingOwnable.owner];
      }
      return [EntityStore.currentUser()];
    }
    return getOwnerChoices();
  }

  private getInitialState(): OwnableSettingsModalState {
    let name;
    let description;
    let collaborators: Array<Collaborator>;
    let owner: Owner;
    let ownerInfo;
    let initialOwnerChoices;
    let collaborations: Array<Collaboration> | undefined;
    if (this.props.existingOwnable != null) {
      ({name, collaborators, owner, ownerInfo} = this.props.existingOwnable);
      if (this.props.enableDescription) {
        description = this.props.existingOwnable.description;
      }
      collaborations = this.props.collaborationState?.collaborations;
    } else {
      name = '';
      description = '';
      initialOwnerChoices = this.getInitialOwnerChoices();
      owner = initialOwnerChoices.find((o) => o.id === this.props.preferredOwnerId) || initialOwnerChoices[0];
      const currentUser = EntityStore.currentUser();
      if (this.props.initialCollaborators != null) {
        collaborators = this.props.initialCollaborators;
      } else if (!BenchlingConfig.ENABLE_AUTO_POPULATE_COLLABORATORS) {
        collaborators = [];
      } else if (hasAllOrgsWithDisablePersonalOwnables() && owner.id !== currentUser.id) {
        collaborators = [makeNewCollaborator(currentUser, getAdminDataPolicy(this.props.dataPolicies!))];
      } else {
        collaborators = getOwnerChoices().map((collaborator) =>
          makeNewCollaborator(collaborator, getDefaultDataPolicy(this.props.dataPolicies!))
        );
      }
      collaborators = collaborators
        .concat(this.getTeamCollaborators(owner, [], makeNewCollaborator))
        .filter((collaborator) => collaborator.id !== owner.id);
      ownerInfo = this.getOwnerInfo(owner, collaborators);
      const translatedCollaborations: Array<Collaboration> = collaborators.flatMap((collaborator) =>
        convertCollaboratorToCollaborations(collaborator as DataPolicyCollaborator, this.props.dataPolicies!)
      );
      collaborations = [
        ...translatedCollaborations,
        convertOwnerToCollaboration(owner, getAdminDataPolicy(this.props.dataPolicies!)),
      ];

      const defaultMemberDataPolicy = this.props.dataPolicies?.find(
        (policy) => policy.id === (owner as Organization)?.defaultOwnableMemberDataPolicyId
      );
      if (defaultMemberDataPolicy !== undefined) {
        collaborations = [
          ...collaborations,
          convertOwnerToCollaboration(owner, defaultMemberDataPolicy, OrganizationPermissionRole.MEMBER),
        ];
      }
      this.props.collaborationActions?.setCollaborations(collaborations);
    }

    return {
      posting: false,
      errors: null,
      name,
      description,
      collaborators,
      owner,
      ownerInfo,
      reviewLifecycleIds: this.props.initialReviewLifecycleIds ?? [],
      // Initialize legacy as true only if that's already been set on an existing project.
      isAuditorBasedReview: isEqual(this.props.initialReviewLifecycleIds, []) ?? false,
    };
  }

  private allowReviewProcessSelection(): boolean {
    return Boolean(
      this.props.supportReviewLifecycleSelection && BenchlingConfig.ENABLE_ENTRY_REVIEW_PROCESSES
    );
  }

  override state: OwnableSettingsModalState = this.getInitialState();
  canManageCollaborators: boolean = this.props.canManageCollaborators ?? !(this.props.isReadOnly ?? false);
  private componentIsMounted = false;

  override componentDidMount(): void {
    this.componentIsMounted = true;
  }

  override componentWillUnmount(): void {
    this.componentIsMounted = false;
  }

  getPotentialOwners(): Array<Owner> {
    let potentialOwners: Array<Owner> = [];
    if (this.props.requiresCreatorOwnership || hasAllOrgsWithDisablePersonalOwnables()) {
      potentialOwners = this.getInitialOwnerChoices();
    } else if (this.props.useCollaborationManagement) {
      const ownerChoices = getOwnerChoices();
      // only show users and organizations that are owners
      potentialOwners = _.union(
        ownerChoices,
        this.props
          .collaborationState!.collaborations.filter(
            (collaboration) =>
              collaboration.party.__typename === 'UserNode' ||
              (collaboration.party.__typename === 'OrganizationNode' &&
                (ownerChoices.some((owner) => owner.id === collaboration.party.id) ||
                  collaboration.fromOwnership))
          )
          .map((collaboration): Owner => ({
            ...collaboration.party,
            avatar_url: collaboration.party.avatarUrl ?? '',
            joinedOn: '',
            url_identifier: collaboration.party.id,
            type:
              collaboration.party.__typename === 'UserNode'
                ? ModelTypeConstants.USER
                : ModelTypeConstants.ORGANIZATION,
          }))
      );
    } else {
      const collaborators = this.state.collaborators.filter(
        (collaborator) =>
          (this.props.existingOwnable?.owner?.id === collaborator.id &&
            collaborator.type === ModelTypeConstants.ORGANIZATION) ||
          collaborator.type === ModelTypeConstants.USER
      );
      potentialOwners = _.union(getOwnerChoices(), collaborators);
    }
    potentialOwners = _.union([this.state.owner], potentialOwners);
    // eslint-disable-next-line no-restricted-syntax
    return _.uniq(potentialOwners, 'id');
  }

  deleteHandler(): void {
    // If this handler is called, props.doDelete will be defined
    // TODO(T4224): ensure that both modals aren't open at once
    const typeName = typeToName(this.props.ownableType).toLowerCase();
    if (this.props.showConfirmDelete) {
      modals
        .confirm(
          `Delete ${capitalize(typeName)}?`,
          `Are you sure you want to delete the ${typeName}? This action is not
        reversible.`,
          'danger'
        )
        .done(() => {
          this.setState({posting: true});
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return (this.props.doDelete! as () => JQueryPromise<any>)()
            .done((response: Response) => {
              notifications.success(
                <>
                  Successfully deleted {typeName} <strong>{this.props.existingOwnable!.name}</strong>.
                </>
              );
              // onRequestClose/onUpdate are parent callbacks that must fire even if this instance has
              // already unmounted.
              this.props.onRequestClose();
              this.props.onUpdate?.();
            })
            .fail((response: Response) => {
              notifications.notifyError(response);
              if (this.componentIsMounted) {
                this.setState({posting: false});
              }
            });
        });
    } else {
      this.props.doDelete!();
    }
  }

  /**
   * We only include `reviewLifecycleApiIds` as part of the payload if:
   * 1. Review processes can even be selected
   * 2. The user actually selected something different than what was already set (applies only to updating projects).
   *    We persist the selected order so order does matter for equality check.
   *
   * This allows us to detect in SourceActions whether we need to make a separate request to the
   * set_project_review_lifecycle endpoint.
   */
  getReviewLifecycleParams(): {reviewLifecycleApiIds?: Array<string>} {
    if (!this.allowReviewProcessSelection()) {
      return {};
    }
    const selectedReviewLifecycleIds = this.state.isAuditorBasedReview ? [] : this.state.reviewLifecycleIds;

    // If the review lifecycle selection has updated, then we include them as params as well.
    // If they were initially empty, and they are still empty, then we don't include them.
    if (!isEqual(this.props.initialReviewLifecycleIds ?? [], selectedReviewLifecycleIds)) {
      return {reviewLifecycleApiIds: selectedReviewLifecycleIds};
    }
    return {};
  }

  closeHandler(): void {
    if (!this.state.posting) {
      this.props.onRequestClose();
    }
  }

  submitHandler(): void {
    if (this.state.posting) {
      return;
    }
    if (this.props.isReadOnly && !this.canManageCollaborators) {
      this.props.onRequestClose();
      return;
    }
    if (this.allowReviewProcessSelection()) {
      this.setState({posting: true});
      confirmReviewLifecycleRetractionIfNeeded({
        initialReviewLifecycleIds: this.props.initialReviewLifecycleIds,
        includesTemplateCollections: false,
        selectedReviewLifecycleIds: this.state.isAuditorBasedReview ? [] : this.state.reviewLifecycleIds,
        supportsLegacyReview: true,
      }).then((confirmed) => {
        if (confirmed) {
          this.performSubmit();
        } else if (this.componentIsMounted) {
          this.setState({posting: false});
        }
      });
      return;
    }
    this.performSubmit();
  }

  performSubmit(): void {
    this.setState({posting: true});
    const updatedOwnerInfo: OwnerInfo = {
      ...this.state.ownerInfo,
      memberDataPolicyId:
        this.props.collaborationState?.collaborations.find(
          (clb) => clb.fromOwnership && clb.organizationRole === OrganizationPermissionRole.MEMBER
        )?.policy.id ?? null,
    };
    Promise.resolve(
      this.props.doSubmit({
        name: this.state.name,
        owner: this.state.owner,
        ownerInfo: this.props.useCollaborationManagement ? updatedOwnerInfo : this.state.ownerInfo,
        description: this.state.description ?? undefined,
        collaborators: this.props.useCollaborationManagement ? undefined : this.state.collaborators,
        collaborations: this.props.collaborationState?.collaborations,
        ...this.getReviewLifecycleParams(),
      })
    )
      .then((response) => {
        this.props.collaborationActions?.refetch?.();
        const action = this.props.existingOwnable != null ? 'updated' : 'created';

        let notification = (
          <>
            Successfully {action} {typeToName(this.props.ownableType)} <strong>{this.state.name}</strong>.
          </>
        );
        if (!isEqual(this.getReviewLifecycleParams(), {})) {
          notification = (
            <>
              {notification} It might take up to a few minutes for review process updates to reflect in
              search.
            </>
          );
        }
        notifications.success(notification);
        // onRequestClose/onUpdate are parent callbacks that must still fire so
        // the parent stops rendering the modal. Only setState needs the guard.
        this.props.onRequestClose();
        this.props.onUpdate?.();
        if (this.props.successCallback != null) {
          const {rootFolder} = response;
          this.props.successCallback({
            id: rootFolder.api_identifier,
            source: response,
            type: rootFolder.type,
            url: rootFolder.url,
          });
          return;
        }
        if (this.props.navigateOnSuccess) {
          navigateToURL(response.url, {replace: this.props.existingOwnable != null});
        }
      })
      .catch((response) => {
        if (this.componentIsMounted) {
          this.setState({posting: false});
        }

        const responseJSON = response.responseJSON;
        if (responseJSON == null) {
          return;
        }

        if (responseJSON.errors) {
          if (this.componentIsMounted) {
            this.setState({errors: responseJSON.errors});
          }
        } else {
          notifications.notifyError(response);
        }
      });
  }

  private getTeamCollaborators<T>(
    owner: Owner,
    collaboratorIds: Array<string>,
    makeNewCollaborations: (party: Party | Owner, policy: DataPolicy, adminPolicy?: DataPolicy) => Array<T>
  ): Array<T> {
    const currentUser = EntityStore.currentUser();
    let teamCollaborators: Array<T> = [];
    // If the owner of the project is an organization and IDENTITY_ENABLE_AUTO_ADD_TEAM_COLLABORATORS is
    // enabled, we want to add all teams in the organization that the user is a part as collaborators of
    // the project.
    if (
      BenchlingConfig.IDENTITY_ENABLE_AUTO_ADD_TEAM_COLLABORATORS &&
      owner.type === ModelTypeConstants.ORGANIZATION
    ) {
      const teamsToAdd = currentUser.teams.reduce((teams: Array<Team>, team: Team) => {
        if (team.organizationId === owner.id && !collaboratorIds.includes(team.id)) {
          teams.push(team);
        }
        return teams;
      }, []);
      const policy = getDefaultDataPolicy(this.props.dataPolicies!);
      teamCollaborators = teamsToAdd.flatMap((team: Team): Array<T> => makeNewCollaborations(team, policy));
    }
    return teamCollaborators;
  }

  private getOwnerInfo(owner: Owner, collaborators: Array<Collaborator>): OwnerInfo {
    return {
      type: owner.type!,
      memberDataPolicyId:
        owner.type === ModelTypeConstants.ORGANIZATION
          ? this.getMemberDataPolicyId(owner as Organization, collaborators)
          : null,
    };
  }

  private getOwnerInfoFromCollaborations(owner: Owner, collaborations: Array<Collaboration>): OwnerInfo {
    return {
      type: owner.type!,
      memberDataPolicyId:
        owner.type === ModelTypeConstants.ORGANIZATION
          ? (collaborations.find(
              (clb) =>
                clb.fromOwnership &&
                clb.organizationRole === OrganizationPermissionRole.MEMBER &&
                clb.policy.id !== NONE_POLICY.id
            )?.policy.id ?? null)
          : null,
    };
  }

  private getMemberDataPolicyId(
    organization: Organization,
    collaborators: Array<Collaborator>
  ): string | null {
    const orgCollaborator = collaborators.find((c) => c.id === organization.id);
    if (orgCollaborator) {
      return getCollaboratorMemberPolicyId(orgCollaborator as GroupCollaborator);
    }
    return organization.defaultOwnableMemberDataPolicyId || null;
  }

  private selectOwner(newOwnerId: string): void {
    const oldOwner = this.state.owner;

    if (oldOwner.id === newOwnerId) {
      return;
    }
    const newOwner = _.findWhere(this.getPotentialOwners(), {id: newOwnerId});
    if (!this.props.useCollaborationManagement) {
      // Remove new owner and add old owner to collaborators.
      let newCollaborators = this.state.collaborators.filter((c) => c.id !== newOwnerId);

      newCollaborators = newCollaborators.concat(
        this.getTeamCollaborators(
          newOwner!,
          newCollaborators.map((clb) => clb.id),
          makeNewCollaborator
        )
      );

      if (this.state.ownerInfo.type === ModelTypeConstants.ORGANIZATION) {
        // Preserve the org's member data policy when changing it to be a collaborator
        newCollaborators.push(
          makeNewCollaborator(
            oldOwner,
            this.props.dataPolicies!.find((p) => p.id === this.state.ownerInfo.memberDataPolicyId!)!,
            getAdminDataPolicy(this.props.dataPolicies!)
          )
        );
      } else {
        newCollaborators.push(makeNewCollaborator(oldOwner, getAdminDataPolicy(this.props.dataPolicies!)));
      }

      this.setState({
        owner: newOwner,
        ownerInfo: this.getOwnerInfo(newOwner!, this.state.collaborators),
        collaborators: newCollaborators,
      });
    } else {
      const adminPolicy = getAdminDataPolicy(this.props.dataPolicies!);
      const defaultMemberDataPolicy = this.props.dataPolicies?.find(
        (policy) => policy.id === (newOwner as Organization)?.defaultOwnableMemberDataPolicyId
      );
      const newOwnerExistingMemberCollaborations = this.props.collaborationState!.collaborations.filter(
        (c) => c.party.id === newOwnerId && c.organizationRole === OrganizationPermissionRole.MEMBER
      );
      // If the new owner is an organization and an existing collaborator, preserve the org's member data policy.
      // If the new owner is an organization and not an existing collaborator - and hence has no associated policy, assign the organization's default member policy to the members of the organization.
      const groupOwnerMemberDataPolicy =
        newOwnerExistingMemberCollaborations.length > 0
          ? this.props.dataPolicies?.find(
              (policy) => policy.id === newOwnerExistingMemberCollaborations[0].policy.id
            )
          : defaultMemberDataPolicy;
      const newOwnerCollaborations: Array<Collaboration> = isUser(newOwner!)
        ? makeNewCollaboration(newOwner, adminPolicy)
        : makeNewCollaboration(newOwner!, groupOwnerMemberDataPolicy, adminPolicy);
      const nonOwnerCollaborations = this.props.collaborationState!.collaborations.filter(
        (c) => c.party.id !== newOwnerId
      );

      // Add teams of an organization if IDENTITY_ENABLE_AUTO_ADD_TEAM_COLLABORATORS
      const newTeamClbs = this.getTeamCollaborators(
        newOwner!,
        nonOwnerCollaborations.map((clb) => clb.party.id),
        makeNewCollaboration
      );

      const newCollaborations: Array<Collaboration> = [
        ...nonOwnerCollaborations.map((clb) => ({
          ...clb,
          fromOwnership: false,
        })),
        ...newTeamClbs,
        ...newOwnerCollaborations.map((clb) => ({
          ...clb,
          fromOwnership: true,
          auditor: isUser(newOwner!) ? true : null,
        })),
      ];

      this.props.collaborationActions?.setCollaborations(newCollaborations);
      this.setState({
        owner: newOwner,
        ownerInfo: this.getOwnerInfoFromCollaborations(newOwner!, newCollaborations),
      });
    }
  }

  private getTitle(typeTitle: string): string {
    if (this.props.title) {
      return this.props.title;
    }
    return this.props.existingOwnable != null ? `${capwords(typeTitle)} Settings` : `Create ${typeTitle}`;
  }

  private renderName(): JSX.Element {
    return (
      <FormGroup
        hasError={this.state.errors?.name != null}
        dataTestAttrs={{component: 'OwnableSettingsForm', element: 'nameFieldWrapper'}}
      >
        <Label htmlFor="name" className="required">
          Name
        </Label>
        <FormInput
          id="name"
          disabled={this.props.isReadOnly || this.state.posting}
          name="name"
          type="text"
          value={this.state.name}
          focusOnMount={true}
          selectOnMount={true}
          onChange={(e: InputChangeEvent) => {
            this.setState({name: e.target.value});
          }}
          {...getTestProps({component: 'OwnableSettingsForm', element: 'nameFieldInput'})}
        />
        <LegacyFieldError
          dataTestAttrs={{component: 'OwnableSettingsForm', element: 'nameFieldError'}}
          message={this.state.errors?.name}
        />
      </FormGroup>
    );
  }

  private shouldRenderSelectOwnerField(): boolean {
    return this.props.requiresCreatorOwnership || this.getPotentialOwners().length > 1;
  }

  private renderOwner(): JSX.Element {
    const showTooltip = this.props.disabledOwnerTooltipText && !this.props.disabledOwnerHelpContent;
    return (
      <>
        <Popover
          placement={FloatingPlacement.BOTTOM}
          isDisabled={!showTooltip || this.state.posting}
          renderContent={() => <span>{this.props.disabledOwnerTooltipText}</span>}
          interaction={FloatingInteraction.HOVER}
        >
          <SelectOwnerField
            error={this.state.errors?.owner}
            ownableType={this.props.ownableType}
            isReadOnly={this.state.posting || this.props.disableChangeOwner || !this.canManageCollaborators}
            ownerId={this.state.owner.id}
            selectOwner={(newOwnerId) => {
              this.selectOwner(newOwnerId);
              // We reset the review lifecycle id to the default when the owner is changed to handle the case
              // where the new owner might not have access to the selected review lifecycle
              if (this.allowReviewProcessSelection()) {
                this.setState({reviewLifecycleIds: []});
              }
            }}
            potentialOwners={this.getPotentialOwners()}
            helpMessage={getSelectOwnerHelpMessage(
              this.props.ownableType,
              true,
              this.props.ownableType === ModelTypeConstants.WORKLIST
                ? OwnershipType.CREATOR
                : OwnershipType.USER_AND_ORG
            )}
            collaboratorsAllowed={true}
          />
        </Popover>
        {this.props.disabledOwnerHelpContent}
      </>
    );
  }

  private renderDescription(): JSX.Element {
    return (
      <FormGroup
        hasError={this.state.errors?.description != null}
        dataTestAttrs={{component: 'OwnableSettingsForm', element: 'descriptionFieldWrapper'}}
      >
        <Label htmlFor="description">Description</Label>
        <textarea
          id="description"
          name="description"
          disabled={this.props.isReadOnly || this.state.posting}
          value={this.state.description ? this.state.description : ''}
          onChange={(e) => {
            this.setState({description: e.target.value});
          }}
          rows={3}
        />
        <LegacyFieldError
          dataTestAttrs={{component: 'OwnableSettingsForm', element: 'descriptionFieldError'}}
          message={this.state.errors?.description}
        />
      </FormGroup>
    );
  }

  selectReviewLifecycleId = (reviewLifecycleId: string): void => {
    if (!this.state.reviewLifecycleIds.includes(reviewLifecycleId)) {
      this.setState({reviewLifecycleIds: this.state.reviewLifecycleIds.concat([reviewLifecycleId])});
    } else {
      this.setState({
        reviewLifecycleIds: this.state.reviewLifecycleIds.filter((rlcId) => rlcId !== reviewLifecycleId),
      });
    }
  };

  removeReviewLifecycleId = (reviewLifecycleId: string): void => {
    this.setState({
      reviewLifecycleIds: (this.state.reviewLifecycleIds ?? []).filter((id) => id !== reviewLifecycleId),
    });
  };

  disableSave(): boolean {
    return (
      this.state.posting ||
      this.props.disableSave ||
      !this.state.name ||
      (this.allowReviewProcessSelection() &&
        // If auditor based review is not selected, but review processes have also not been chosen, disable review.
        this.state.reviewLifecycleIds.length === 0 &&
        !this.state.isAuditorBasedReview)
    );
  }

  getSaveButtonTooltip(): string | null {
    return !this.state.posting && this.disableSave() ? 'Please fill out required fields' : null;
  }

  override render(): JSX.Element {
    const typeTitle = typeToName(this.props.ownableType);
    const buttons = [];
    if (this.props.isReadOnly && !this.canManageCollaborators) {
      buttons.push({
        text: 'Close',
        style: ButtonStyle.SECONDARY,
        submit: true,
        dataTestAttrs: {component: 'OwnableSettingModal', element: 'closeButton'},
      });
    } else {
      buttons.push({
        text: (() => {
          if (this.state.posting) {
            return 'Loading...';
          }
          if (this.props.existingOwnable != null) {
            return 'Save';
          }
          return `Create ${typeTitle}`;
        })(),
        style: ButtonStyle.PRIMARY,
        submit: true,
        disabled: this.disableSave(),
        tooltipTitle: this.getSaveButtonTooltip(),
        dataTestAttrs: {component: 'OwnableSettingsForm', element: 'createButton'},
      });
    }

    // TODO(BNCH-39788) Simplify conditional button logic.
    // This logic below is very nasty, because different models have different rules
    // for when they can be archived/deleted, and this component was made without using
    // the permissions JSON to decide what buttons to show.
    const shouldShowArchiveButton =
      this.props.existingOwnable != null && this.props.ownableType === ModelTypeConstants.PROJECT;

    const shouldShowDeleteButtonForNonProjectOwnable =
      this.props.existingOwnable != null &&
      this.props.ownableType !== ModelTypeConstants.PROJECT &&
      !this.props.isReadOnly &&
      this.props.doDelete;

    if (this.props.doArchive && shouldShowArchiveButton) {
      buttons.push({
        text: `Archive ${typeTitle}`,
        style: ButtonStyle.NEGATIVE,
        pullLeft: true,
        onClick: this.props.doArchive,
        disabled: this.state.posting,
        dataTestAttrs: {component: 'OwnableSettingModal', element: 'archiveProjectButton'},
      });
    } else if (this.props.doUnarchive && shouldShowArchiveButton) {
      buttons.push({
        text: `Unarchive ${typeTitle}`,
        style: ButtonStyle.NEGATIVE,
        pullLeft: true,
        onClick: this.props.doUnarchive,
        disabled: this.state.posting,
        dataTestAttrs: {component: 'OwnableSettingModal', element: 'unarchiveProjectButton'},
      });
    } else if (shouldShowDeleteButtonForNonProjectOwnable) {
      buttons.push({
        text: `Delete ${typeTitle}`,
        style: ButtonStyle.NEGATIVE,
        pullLeft: true,
        onClick: () => {
          this.deleteHandler();
        },
        disabled: this.state.posting,
      });
    }

    const CustomSettingsComponent = this.props.customSettingsComponent ?? null;

    return (
      <Modal
        title={this.getTitle(typeTitle)}
        onRequestClose={() => {
          this.closeHandler();
        }}
        onRequestSubmit={() => {
          this.submitHandler();
        }}
        buttons={buttons}
        className="_test-ownable-settings-modal"
        dataTestAttrs={{component: 'OwnableSettingsModal', element: 'modal', key: typeTitle}}
        analyticsModule={this.props.analyticsModule}
        analyticsModalType={this.props.analyticsModalType}
        analyticsMetadata={this.props.analyticsMetadata}
      >
        <Row>
          <Col md={12} className="col-centered">
            <Row>
              {this.props.enableName && (
                <Col sm={this.shouldRenderSelectOwnerField() ? 7 : 12}>{this.renderName()}</Col>
              )}
              {this.shouldRenderSelectOwnerField() && <Col sm={5}>{this.renderOwner()}</Col>}
            </Row>
            {this.props.enableDescription ? this.renderDescription() : null}
            {CustomSettingsComponent ? (
              <CustomSettingsComponent isPosting={this.state.posting} owner={this.state.owner} />
            ) : null}
            {this.props.supportReviewLifecycleSelection ? (
              <ReviewLifecycleAndCollaboratorsSection
                isAuditorBasedReview={this.state.isAuditorBasedReview}
                setIsAuditorBasedReview={(isAuditorBasedReview: boolean) => {
                  this.setState({isAuditorBasedReview});
                }}
                reviewLifecycleIds={this.state.reviewLifecycleIds}
                isMultiReviewProcessSelector={true}
                selectReviewLifecycleId={(reviewLifecycleId: string) => {
                  this.selectReviewLifecycleId(reviewLifecycleId);
                }}
                removeReviewLifecycleId={this.removeReviewLifecycleId}
                collaborators={this.state.collaborators}
                isReadOnly={this.props.isReadOnly! || this.state.posting}
                ownableId={this.props.ownableId}
                owner={this.state.owner}
                ownerInfo={this.state.ownerInfo}
                onLeave={this.props.onLeave}
                onRequestClose={this.props.onRequestClose}
                onUpdate={(data: UpdateArgs) => {
                  this.setState(data);
                }}
                initialReviewLifecycleIds={this.props.initialReviewLifecycleIds}
                showWarningMessageOnReviewLifecycleEdit={this.props.showWarningMessageOnReviewLifecycleEdit}
                collaborationState={this.props.collaborationState}
                collaborationActions={this.props.collaborationActions}
                policeableName={this.state.name}
                canManageCollaborators={this.canManageCollaborators ?? !this.props.isReadOnly}
              />
            ) : this.props.useCollaborationManagement ? (
              <CollaborationManagementSection
                {...this.props.collaborationState!}
                {...this.props.collaborationActions!}
                policeableName={this.state.name}
                renderAuditorColumn={this.props.allowAuditorToggling}
                closeParentModal={this.props.onRequestClose}
              />
            ) : (
              <OwnableCollaboratorsSection
                // TODO(BNCH-42025): Should convert the union type that state.collaborators will be to Array<Collaborator>
                collaborators={this.state.collaborators}
                enableAuditors={this.props.allowAuditorToggling}
                isReadOnly={!this.canManageCollaborators}
                ownableId={this.props.ownableId}
                owner={this.state.owner}
                ownerInfo={this.state.ownerInfo}
                onLeave={this.props.onLeave}
                onUpdate={(data: UpdateArgs) => {
                  this.setState(data);
                }}
              />
            )}
          </Col>
        </Row>
      </Modal>
    );
  }
}

const ConnectedOwnableSettingsModal = connect(mapStateToProps)(OwnableSettingsModal);

export default ConnectedOwnableSettingsModal;

export function OwnableSettingsWithCollaborationsModal(props: OwnableSettingsModalProps): JSX.Element {
  const [collaborationState, collaborationActions] = useCollaborationManagementState(props.ownableId);
  if (collaborationState.queryResultType === QueryResultType.ERROR) {
    notifications.error('Could not load collaborators; try reloading the page.');
    return <></>;
  } else if (collaborationState.queryResultType !== QueryResultType.SUCCESS) {
    return <></>;
  }

  const _handleSave = (data: UpdateOwnableParams): PromiseLike<void> =>
    props.doSubmit({
      ...data,
      collaborations: isEqual(data.collaborations, collaborationState.initialCollaborations)
        ? undefined
        : data.collaborations,
    });
  return (
    <ConnectedOwnableSettingsModal
      {...props}
      doSubmit={_handleSave}
      collaborationState={collaborationState}
      collaborationActions={collaborationActions}
      useCollaborationManagement={true}
    />
  );
}
